import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { QrCode, Eye, ShoppingBag, XCircle, CheckCircle2 } from "lucide-react";
import HelpVideoButton from "@/components/HelpVideoButton";
import { MobileSlip } from "@/components/PrintDocs";
import { toast } from "sonner";

/** Derive slip chip statuses from the single-pass lifecycle + animal state. */
function statusFrom(gp, animal) {
  const lifecycle = gp.status || (gp.scanned_at ? "entered" : "registered");
  const entryStatus = lifecycle === "registered" ? "issued" : "scanned";
  const exitStatus = animal?.exit_status || (lifecycle === "exited" ? "sold" : gp.pass_type === "exit" ? (gp.exit_status || "sold") : "pending");
  return { entryStatus, exitStatus };
}

export default function HelperGatepasses() {
  const [list, setList] = useState([]);
  const [view, setView] = useState(null);       // the gatepass being viewed
  const [animal, setAnimal] = useState(null);   // its animal
  const [purchaser, setPurchaser] = useState(null);
  const [sellerDoc, setSellerDoc] = useState(null);
  const [saleOpen, setSaleOpen] = useState(false);
  const [saleForm, setSaleForm] = useState({ name: "", mobile: "", address: "", reason: "" });
  // Audited purchaser correction at exit (replaces the registration-time purchaser).
  const [editPurchaser, setEditPurchaser] = useState(false);
  const [saving, setSaving] = useState(false);
  // Server-stored receipt identity for the viewed pass (haat/owner/helper names).
  const [slipReceipt, setSlipReceipt] = useState(null);
  const [receiptCtx, setReceiptCtx] = useState({});

  const load = () => api.get("/helper/gatepasses").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/helper/receipt-context").then((r) => setReceiptCtx(r.data || {})).catch(() => {});
  }, []);

  const open = async (gp) => {
    setView(gp);
    setAnimal(null); setPurchaser(null); setSellerDoc(null); setSlipReceipt(null);
    api.get("/helper/cash")
      .then((r) => setSlipReceipt((r.data || []).find((c) => c.animal_id === gp.animal_id) || null))
      .catch((err) => console.debug("receipt lookup failed", err));
    try {
      const r = await api.get(`/helper/animals/${gp.animal_id}`);
      setAnimal(r.data);
      if (r.data.purchaser_id) {
        try {
          const ps = await api.get("/helper/purchasers");
          setPurchaser(ps.data.find((p) => p.purchaser_id === r.data.purchaser_id) || null);
        } catch (err) { console.debug("purchaser lookup failed", err); }
      }
      if (r.data.seller_id) {
        try {
          const ss = await api.get("/helper/sellers");
          setSellerDoc(ss.data.find((s) => s.seller_id === r.data.seller_id) || null);
        } catch (err) { console.debug("seller lookup failed", err); }
      }
    } catch (err) { console.debug("animal lookup failed", err); }
  };

  const completeSale = async (outcome) => {
    if (!animal) return;
    // Purchaser captured at registration → no re-entry needed at exit
    // (unless the helper explicitly corrects it — audited on the server).
    const hasRegPurchaser = !!(animal.purchaser_id && purchaser);
    const useRegPurchaser = hasRegPurchaser && !editPurchaser;
    if (outcome === "sold" && !useRegPurchaser && (!saleForm.name.trim() || !saleForm.mobile.trim())) {
      toast.error("Purchaser name and mobile are required");
      return;
    }
    setSaving(true);
    try {
      const body = outcome === "sold"
        ? (useRegPurchaser
            ? { exit_status: "sold" } // backend reuses the registration-time purchaser
            : { exit_status: "sold", purchaser_name: saleForm.name, purchaser_mobile: saleForm.mobile, purchaser_address: saleForm.address,
                ...(editPurchaser ? { correction_reason: saleForm.reason || "" } : {}) })
        : { exit_status: "unsold", exit_reason: saleForm.address || "" };
      const r = await api.post(`/helper/animals/${animal.animal_id}/complete-sale`, body);
      toast.success(outcome === "sold" ? "Sale recorded — scan the same pass QR at the gate for exit" : "Marked unsold — scan the same pass QR at the gate for exit");
      // Single-pass model: keep showing the SAME pass (refreshed from server).
      if (r.data.gatepass) setView({ ...r.data.gatepass, animal_id: animal.animal_id });
      setAnimal(r.data.animal || animal);
      setPurchaser(r.data.purchaser || null);
      setSaleOpen(false);
      setSaleForm({ name: "", mobile: "", address: "", reason: "" });
      setEditPurchaser(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not complete sale");
    } finally {
      setSaving(false);
    }
  };

  const { entryStatus, exitStatus } = view ? statusFrom(view, animal) : { entryStatus: "issued", exitStatus: "pending" };
  const canCompleteSale = view?.pass_type === "entry" && animal && !animal.exit_status;

  return (
    <DashboardShell
      title="Gate Passes"
      subtitle={`${list.length} issued`}
      action={
        <div className="flex gap-2">
          <HelpVideoButton screen="entry_pass" labelKey="help.entryPass" />
          <HelpVideoButton screen="exit_pass" labelKey="help.exitPass" />
        </div>
      }
    >
      <div className="am-card p-2 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
              <th className="px-4 py-3">Pass No.</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Animal</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Issued</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((g) => (
              <tr key={g.gatepass_id} className="border-b last:border-0">
                <td className="px-4 py-3 font-mono">{g.pass_number}</td>
                <td className="px-4 py-3 capitalize"><span className="am-chip">{g.pass_type}</span></td>
                <td className="px-4 py-3 font-mono text-xs">{g.animal_id}</td>
                <td className="px-4 py-3">
                  {(() => {
                    const s = g.status || (g.scanned_at ? "entered" : g.pass_type === "exit" ? (g.exit_status || "sold") : "registered");
                    return <span className={`am-chip ${s !== "registered" ? "am-chip-ok" : ""}`} data-testid={`gatepass-status-${g.pass_number}`}>{s}</span>;
                  })()}
                </td>
                <td className="px-4 py-3 text-xs font-mono">{(g.created_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => open(g)}
                    data-testid={`gatepass-view-${g.pass_number}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> View & Print
                  </Button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="py-12 text-center text-muted-foreground">
                  <QrCode className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  No gate passes yet. Issue one from the New Registration page.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Slip modal */}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Gate Pass · {view?.pass_number}</DialogTitle>
            <DialogDescription>Print, save or share the slip. The SAME QR is scanned at entry and again at exit.</DialogDescription>
          </DialogHeader>
          {view && (view.entry_time || view.exit_time || view.scanned_at) && (
            <div className="text-xs text-muted-foreground font-mono flex flex-wrap gap-x-4" data-testid="gatepass-times">
              {(view.entry_time || view.scanned_at) && <span>Entry: {new Date(view.entry_time || view.scanned_at).toLocaleString("en-IN")}</span>}
              {view.exit_time && <span>Exit: {new Date(view.exit_time).toLocaleString("en-IN")}</span>}
            </div>
          )}
          {view && (
            <div className="space-y-3">
              <MobileSlip
                variant={view.pass_type === "exit" ? "exit" : (animal?.exit_status ? "exit" : "entry")}
                haatName={slipReceipt?.haat_name || receiptCtx.haat_name}
                subscriberName={slipReceipt?.owner_name || receiptCtx.owner_name}
                helperName={slipReceipt?.helper_name || receiptCtx.helper_name}
                entryTime={view.entry_time || view.scanned_at}
                exitTime={view.exit_time}
                receiptNumber={slipReceipt?.receipt_number}
                amount={slipReceipt?.amount}
                feeBreakdown={slipReceipt?.fee_breakdown}
                passNumber={view.pass_number}
                qrPayload={view.qr_payload}
                registrationNumber={animal?.registration_number}
                animalId={animal?.registration_number}
                animalType={animal?.animal_type}
                animalBreed={animal?.breed}
                animalGender={animal?.gender}
                animalColour={animal?.colour}
                salePrice={animal?.sale_price}
                sellerName={sellerDoc?.name}
                sellerMobile={sellerDoc?.mobile}
                sellerVillage={sellerDoc?.village || sellerDoc?.address}
                purchaserName={purchaser?.name}
                purchaserMobile={purchaser?.mobile}
                purchaserVillage={purchaser?.village || purchaser?.address}
                saleCompleted={!!animal?.exit_status}
                timestamp={view.created_at}
                entryStatus={entryStatus}
                exitStatus={exitStatus}
              />

              {canCompleteSale && (
                <div className="pt-2 border-t no-print">
                  <Button
                    className="am-btn-primary w-full"
                    onClick={() => { setEditPurchaser(false); setSaleOpen(true); }}
                    data-testid="gatepass-complete-sale"
                  >
                    <ShoppingBag className="w-4 h-4 mr-1.5" /> Complete Sale
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Complete-sale mini form */}
      <Dialog open={saleOpen} onOpenChange={setSaleOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Complete Sale</DialogTitle>
            <DialogDescription>
              {animal?.purchaser_id && purchaser && !editPurchaser
                ? "Purchaser was captured at registration — verify below and confirm. An exit pass will be issued automatically."
                : editPurchaser
                  ? "Correct the purchaser for this sale. The change is recorded in the audit log."
                  : "Capture the purchaser or mark this animal as unsold. An exit pass will be issued automatically."}
            </DialogDescription>
          </DialogHeader>
          {animal?.purchaser_id && purchaser && !editPurchaser ? (
            /* Registration-time purchaser — READ-ONLY verification, no re-entry */
            <div className="rounded-2xl border bg-accent/50 p-4 space-y-1.5" data-testid="sale-purchaser-readonly">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                Purchaser (from registration)
              </div>
              <div className="font-bold" data-testid="sale-purchaser-readonly-name">{purchaser.name || "—"}</div>
              <div className="text-sm font-mono text-muted-foreground" data-testid="sale-purchaser-readonly-mobile">
                {purchaser.mobile ? `+91 ${purchaser.mobile}` : "—"}
              </div>
              <div className="text-sm text-muted-foreground" data-testid="sale-purchaser-readonly-address">
                {purchaser.village || purchaser.address || "—"}
              </div>
              <button
                type="button"
                data-testid="sale-purchaser-edit-btn"
                onClick={() => setEditPurchaser(true)}
                className="pt-1 text-xs font-semibold text-primary hover:underline"
              >
                Wrong purchaser? Change it (audited)
              </button>
            </div>
          ) : (
          <div className="space-y-3">
            {editPurchaser && (
              <button
                type="button"
                data-testid="sale-purchaser-edit-cancel"
                onClick={() => setEditPurchaser(false)}
                className="text-xs font-semibold text-muted-foreground hover:text-primary"
              >
                ← Keep the registered purchaser
              </button>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold">Purchaser name</Label>
              <Input
                value={saleForm.name}
                onChange={(e) => setSaleForm({ ...saleForm, name: e.target.value })}
                placeholder="Purchaser full name"
                data-testid="sale-purchaser-name"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold">Mobile</Label>
              <Input
                value={saleForm.mobile}
                onChange={(e) => setSaleForm({ ...saleForm, mobile: e.target.value.replace(/\D/g, "").slice(0, 15) })}
                placeholder="10-digit mobile"
                inputMode="numeric"
                data-testid="sale-purchaser-mobile"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-bold">Village / Address</Label>
              <Input
                value={saleForm.address}
                onChange={(e) => setSaleForm({ ...saleForm, address: e.target.value })}
                placeholder="Optional"
                data-testid="sale-purchaser-village"
              />
            </div>
            {editPurchaser && (
              <div>
                <Label className="text-xs uppercase tracking-widest font-bold">Reason for change</Label>
                <Input
                  value={saleForm.reason}
                  onChange={(e) => setSaleForm({ ...saleForm, reason: e.target.value })}
                  placeholder="e.g. wrong buyer selected at registration"
                  data-testid="sale-purchaser-edit-reason"
                />
              </div>
            )}
          </div>
          )}
          <DialogFooter className="grid grid-cols-2 gap-2 mt-4">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => completeSale("unsold")}
              disabled={saving}
              data-testid="sale-unsold-btn"
            >
              <XCircle className="w-4 h-4 mr-1.5" /> Mark Unsold
            </Button>
            <Button
              className="am-btn-primary"
              onClick={() => completeSale("sold")}
              disabled={saving}
              data-testid="sale-sold-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> Confirm Sale
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
